import React, { useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import {BarChart3, FileText, Calendar, Download, X, TrendingUp, Users, DollarSign, FileCheck} from 'lucide-react'
import { useGuias } from "../context/GuiasContext"

export default function Relatorios() {
  const { guias, pagamentos, feirantes, aforamentos } = useGuias()
  const [modalAberto, setModalAberto] = useState<"guias" | "mensal" | "anual" | "financeiro" | null>(null)
  const [dataInicio, setDataInicio] = useState("")
  const [dataFim, setDataFim] = useState("")

  const gerarRelatorioGuias = () => {
    setModalAberto("guias")
  }

  const gerarRelatorioMensal = () => {
    setModalAberto("mensal")
  }

  const gerarRelatorioAnual = () => {
    setModalAberto("anual")
  }

  const gerarRelatorioFinanceiro = () => {
    setModalAberto("financeiro")
  }

  const [dataInicioFinanceiro, setDataInicioFinanceiro] = useState("")
  const [dataFimFinanceiro, setDataFimFinanceiro] = useState("")

  const imprimirRelatorio = () => {
    const printWindow = window.open("", "_blank")
    if (!printWindow) return

    let htmlContent = ""

    // Gerar HTML baseado no tipo de relatório
    if (modalAberto === "guias") {
      htmlContent = gerarHTMLRelatorioGuias()
    } else if (modalAberto === "mensal") {
      htmlContent = gerarHTMLRelatorioMensal()
    } else if (modalAberto === "anual") {
      htmlContent = gerarHTMLRelatorioAnual()
    } else if (modalAberto === "financeiro") {
      htmlContent = gerarHTMLRelatorioFinanceiro()
    }

    printWindow.document.write(htmlContent)
    printWindow.document.close()
  }

  const gerarHTMLRelatorioGuias = () => {
    // Filtrar guias por período se houver datas selecionadas
    let guiasFiltradas = guias
    if (dataInicio && dataFim) {
      const inicio = new Date(dataInicio)
      const fim = new Date(dataFim)
      fim.setHours(23, 59, 59, 999) // Incluir o dia inteiro
      
      guiasFiltradas = guias.filter(g => {
        const dataGuia = new Date(g.createdAt || new Date())
        return dataGuia >= inicio && dataGuia <= fim
      })
    }

    const linhasGuias = guiasFiltradas.map(guia => `
      <tr>
        <td style="padding: 12px; border: 1px solid #d1d5db; text-align: left;">${guia.numeroObito || "-"}</td>
        <td style="padding: 12px; border: 1px solid #d1d5db; text-align: left;">${guia.nomeFalecido || "-"}</td>
        <td style="padding: 12px; border: 1px solid #d1d5db; text-align: left;">${guia.dataSepultamento || "-"}</td>
        <td style="padding: 12px; border: 1px solid #d1d5db; text-align: left;">${guia.cemiterio || "-"}</td>
      </tr>
    `).join("")

    return `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8">
          <title>Relatório de Guias</title>
          <style>
            body { margin: 0; padding: 40px; font-family: Arial, sans-serif; }
            .header { text-align: center; margin-bottom: 30px; padding-bottom: 20px; border-bottom: 3px solid #004161; }
            .header h1 { color: #004161; margin: 0 0 10px 0; font-size: 28px; }
            .header p { color: #6b7280; margin: 5px 0; }
            .stats { display: grid; grid-template-columns: repeat(4, 1fr); gap: 20px; margin: 30px 0; }
            .stat-card { background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%); padding: 20px; border-radius: 8px; text-align: center; border: 2px solid #bae6fd; }
            .stat-number { font-size: 32px; font-weight: bold; color: #004161; margin-bottom: 5px; }
            .stat-label { font-size: 14px; color: #6b7280; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th { background: #004161; color: white; padding: 12px; text-align: left; font-weight: 600; }
            td { padding: 12px; border: 1px solid #d1d5db; }
            tr:nth-child(even) { background: #f9fafb; }
            .footer { margin-top: 40px; text-align: center; color: #9ca3af; font-size: 12px; border-top: 1px solid #e5e7eb; padding-top: 20px; }
            @media print { body { padding: 20px; } }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>📋 RELATÓRIO COMPLETO DE GUIAS</h1>
            <p>Sistema de Gerenciamento Municipal</p>
            <p>Gerado em ${new Date().toLocaleDateString("pt-BR")} às ${new Date().toLocaleTimeString("pt-BR")}</p>
            ${dataInicio && dataFim ? `<p style="color: #004161; font-weight: 600;">Período: ${new Date(dataInicio + 'T00:00:00').toLocaleDateString("pt-BR")} a ${new Date(dataFim + 'T23:59:59').toLocaleDateString("pt-BR")}</p>` : ""}
          </div>

          <div class="stats">
            <div class="stat-card">
              <div class="stat-number">${guiasFiltradas.length}</div>
              <div class="stat-label">Total de Guias</div>
            </div>
            <div class="stat-card">
              <div class="stat-number">${pagamentosPagos}</div>
              <div class="stat-label">Pagamentos Confirmados</div>
            </div>
            <div class="stat-card">
              <div class="stat-number">${pagamentosPendentes}</div>
              <div class="stat-label">Pagamentos Pendentes</div>
            </div>
            <div class="stat-card">
              <div class="stat-number">R$ ${valorTotalPagamentos.toFixed(2).replace(".", ",")}</div>
              <div class="stat-label">Valor Total Recebido</div>
            </div>
          </div>

          <h2 style="color: #004161; margin-top: 40px; margin-bottom: 20px; font-size: 20px;">Lista de Guias Emitidas</h2>
          
          ${guiasFiltradas.length === 0 ? '<p style="text-align: center; color: #9ca3af; padding: 40px;">Nenhuma guia cadastrada</p>' : `
            <table>
              <thead>
                <tr>
                  <th>Nº Óbito</th>
                  <th>Nome do Falecido</th>
                  <th>Data Sepultamento</th>
                  <th>Cemitério</th>
                </tr>
              </thead>
              <tbody>
                ${linhasGuias}
              </tbody>
            </table>
          `}

          <div class="footer">
            <p>Documento gerado automaticamente pelo Sistema EMIT</p>
            <p>Este documento possui validade institucional</p>
          </div>

          <script>
            window.onload = function() {
              setTimeout(function() { window.print(); }, 500);
            }
          </script>
        </body>
      </html>
    `
  }

  const gerarHTMLRelatorioMensal = () => {
    const guiasFiltradas = guias.filter(g => {
      const data = new Date(g.createdAt || new Date())
      return data.getMonth() === mesAtual && data.getFullYear() === anoAtual
    })

    const linhasGuias = guiasFiltradas.map(guia => `
      <tr>
        <td style="padding: 12px; border: 1px solid #d1d5db;">${guia.numeroObito || "-"}</td>
        <td style="padding: 12px; border: 1px solid #d1d5db;">${guia.nomeFalecido || "-"}</td>
        <td style="padding: 12px; border: 1px solid #d1d5db;">${guia.dataSepultamento || "-"}</td>
        <td style="padding: 12px; border: 1px solid #d1d5db;">${guia.cemiterio || "-"}</td>
      </tr>
    `).join("")

    return `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8">
          <title>Relatório Mensal</title>
          <style>
            body { margin: 0; padding: 40px; font-family: Arial, sans-serif; }
            .header { text-align: center; margin-bottom: 30px; padding-bottom: 20px; border-bottom: 3px solid #004161; }
            .header h1 { color: #004161; margin: 0 0 10px 0; font-size: 28px; }
            .header p { color: #6b7280; margin: 5px 0; }
            .stats { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; margin: 30px 0; }
            .stat-card { background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%); padding: 25px; border-radius: 8px; text-align: center; border: 2px solid #bbf7d0; }
            .stat-number { font-size: 36px; font-weight: bold; color: #004161; margin-bottom: 8px; }
            .stat-label { font-size: 14px; color: #6b7280; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th { background: #004161; color: white; padding: 12px; text-align: left; font-weight: 600; }
            td { padding: 12px; border: 1px solid #d1d5db; }
            tr:nth-child(even) { background: #f9fafb; }
            .summary { background: #f9fafb; padding: 25px; border-radius: 8px; margin-top: 30px; border: 1px solid #e5e7eb; }
            .summary-item { display: flex; justify-content: space-between; padding: 10px 0; border-bottom: 1px solid #e5e7eb; }
            .summary-item:last-child { border-bottom: none; border-top: 2px solid #004161; padding-top: 15px; font-weight: bold; }
            .footer { margin-top: 40px; text-align: center; color: #9ca3af; font-size: 12px; border-top: 1px solid #e5e7eb; padding-top: 20px; }
            @media print { body { padding: 20px; } }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>📅 RELATÓRIO MENSAL</h1>
            <p>${mesesNomes[mesAtual]} de ${anoAtual}</p>
            <p>Gerado em ${new Date().toLocaleDateString("pt-BR")} às ${new Date().toLocaleTimeString("pt-BR")}</p>
          </div>

          <div class="stats">
            <div class="stat-card">
              <div class="stat-number">${guiasMesAtual}</div>
              <div class="stat-label">Guias no Mês</div>
            </div>
            <div class="stat-card">
              <div class="stat-number">${pagamentosMesAtual.length}</div>
              <div class="stat-label">Pagamentos Confirmados</div>
            </div>
            <div class="stat-card">
              <div class="stat-number">R$ ${valorMesAtual.toFixed(2).replace(".", ",")}</div>
              <div class="stat-label">Valor Recebido</div>
            </div>
          </div>

          <h2 style="color: #004161; margin-top: 40px; margin-bottom: 20px; font-size: 20px;">Guias do Mês</h2>
          
          ${guiasFiltradas.length === 0 ? '<p style="text-align: center; color: #9ca3af; padding: 40px;">Nenhuma guia emitida neste mês</p>' : `
            <table>
              <thead>
                <tr>
                  <th>Nº Óbito</th>
                  <th>Nome do Falecido</th>
                  <th>Data Sepultamento</th>
                  <th>Cemitério</th>
                </tr>
              </thead>
              <tbody>
                ${linhasGuias}
              </tbody>
            </table>
          `}

          <div class="summary">
            <h3 style="color: #004161; margin-top: 0; margin-bottom: 20px;">Resumo do Período</h3>
            <div class="summary-item">
              <span>Total de Guias Emitidas:</span>
              <span>${guiasMesAtual}</span>
            </div>
            <div class="summary-item">
              <span>Pagamentos Confirmados:</span>
              <span>${pagamentosMesAtual.length}</span>
            </div>
            <div class="summary-item">
              <span>Valor Total Recebido:</span>
              <span>R$ ${valorMesAtual.toFixed(2).replace(".", ",")}</span>
            </div>
            <div class="summary-item">
              <span>Taxa de Conversão:</span>
              <span>${guiasMesAtual > 0 ? ((pagamentosMesAtual.length / guiasMesAtual) * 100).toFixed(1) : 0}%</span>
            </div>
          </div>

          <div class="footer">
            <p>Documento gerado automaticamente pelo Sistema EMIT</p>
            <p>Este documento possui validade institucional</p>
          </div>

          <script>
            window.onload = function() {
              setTimeout(function() { window.print(); }, 500);
            }
          </script>
        </body>
      </html>
    `
  }

  const gerarHTMLRelatorioAnual = () => {
    const guiasFiltradas = guias.filter(g => {
      const data = new Date(g.createdAt || new Date())
      return data.getFullYear() === anoAtual
    })

    const linhasGuias = guiasFiltradas.map(guia => `
      <tr>
        <td style="padding: 12px; border: 1px solid #d1d5db;">${guia.numeroObito || "-"}</td>
        <td style="padding: 12px; border: 1px solid #d1d5db;">${guia.nomeFalecido || "-"}</td>
        <td style="padding: 12px; border: 1px solid #d1d5db;">${guia.dataSepultamento || "-"}</td>
        <td style="padding: 12px; border: 1px solid #d1d5db;">${guia.cemiterio || "-"}</td>
      </tr>
    `).join("")

    const linhasDistribuicao = guiasPorMes.map((qtd, index) => `
      <tr>
        <td style="padding: 12px; border: 1px solid #d1d5db;">${mesesNomes[index]}</td>
        <td style="padding: 12px; border: 1px solid #d1d5db; text-align: center;">${qtd}</td>
        <td style="padding: 12px; border: 1px solid #d1d5db; text-align: center;">${guiasAnoAtual > 0 ? ((qtd / guiasAnoAtual) * 100).toFixed(1) : 0}%</td>
      </tr>
    `).join("")

    return `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8">
          <title>Relatório Anual</title>
          <style>
            body { margin: 0; padding: 40px; font-family: Arial, sans-serif; }
            .header { text-align: center; margin-bottom: 30px; padding-bottom: 20px; border-bottom: 3px solid #004161; }
            .header h1 { color: #004161; margin: 0 0 10px 0; font-size: 28px; }
            .header p { color: #6b7280; margin: 5px 0; }
            .stats { display: grid; grid-template-columns: repeat(4, 1fr); gap: 20px; margin: 30px 0; }
            .stat-card { background: linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%); padding: 25px; border-radius: 8px; text-align: center; border: 2px solid #e9d5ff; }
            .stat-number { font-size: 36px; font-weight: bold; color: #004161; margin-bottom: 8px; }
            .stat-label { font-size: 14px; color: #6b7280; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; page-break-inside: avoid; }
            th { background: #004161; color: white; padding: 12px; text-align: left; font-weight: 600; }
            td { padding: 12px; border: 1px solid #d1d5db; }
            tr:nth-child(even) { background: #f9fafb; }
            tfoot { background: #f3f4f6; font-weight: bold; }
            .section { margin-top: 50px; page-break-before: auto; }
            .footer { margin-top: 40px; text-align: center; color: #9ca3af; font-size: 12px; border-top: 1px solid #e5e7eb; padding-top: 20px; }
            @media print { body { padding: 20px; } .section { page-break-before: auto; } }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>📊 RELATÓRIO ANUAL ${anoAtual}</h1>
            <p>Sistema de Gerenciamento Municipal</p>
            <p>Gerado em ${new Date().toLocaleDateString("pt-BR")} às ${new Date().toLocaleTimeString("pt-BR")}</p>
          </div>

          <div class="stats">
            <div class="stat-card">
              <div class="stat-number">${guiasAnoAtual}</div>
              <div class="stat-label">Guias no Ano</div>
            </div>
            <div class="stat-card">
              <div class="stat-number">${pagamentosAnoAtual.length}</div>
              <div class="stat-label">Pagamentos Confirmados</div>
            </div>
            <div class="stat-card">
              <div class="stat-number">R$ ${valorAnoAtual.toFixed(2).replace(".", ",")}</div>
              <div class="stat-label">Valor Total Recebido</div>
            </div>
            <div class="stat-card">
              <div class="stat-number">${totalFeirantes}</div>
              <div class="stat-label">Feirantes Ativos</div>
            </div>
          </div>

          <div class="section">
            <h2 style="color: #004161; margin-bottom: 20px; font-size: 20px;">Distribuição Mensal de Guias</h2>
            <table>
              <thead>
                <tr>
                  <th>Mês</th>
                  <th style="text-align: center;">Quantidade</th>
                  <th style="text-align: center;">Porcentagem</th>
                </tr>
              </thead>
              <tbody>
                ${linhasDistribuicao}
              </tbody>
              <tfoot>
                <tr>
                  <td style="padding: 12px; border: 1px solid #d1d5db;">Total</td>
                  <td style="padding: 12px; border: 1px solid #d1d5db; text-align: center;">${guiasAnoAtual}</td>
                  <td style="padding: 12px; border: 1px solid #d1d5db; text-align: center;">100%</td>
                </tr>
              </tfoot>
            </table>
          </div>

          <div class="section">
            <h2 style="color: #004161; margin-bottom: 20px; font-size: 20px;">Guias do Ano</h2>
            ${guiasFiltradas.length === 0 ? '<p style="text-align: center; color: #9ca3af; padding: 40px;">Nenhuma guia emitida neste ano</p>' : `
              <table>
                <thead>
                  <tr>
                    <th>Nº Óbito</th>
                    <th>Nome do Falecido</th>
                    <th>Data Sepultamento</th>
                    <th>Cemitério</th>
                  </tr>
                </thead>
                <tbody>
                  ${linhasGuias}
                </tbody>
              </table>
            `}
          </div>

          <div class="footer">
            <p>Documento gerado automaticamente pelo Sistema EMIT</p>
            <p>Este documento possui validade institucional</p>
          </div>

          <script>
            window.onload = function() {
              setTimeout(function() { window.print(); }, 500);
            }
          </script>
        </body>
      </html>
    `
  }

  const gerarHTMLRelatorioFinanceiro = () => {
    // Filtrar pagamentos por período se houver datas selecionadas
    let pagamentosFiltrados = pagamentos
    if (dataInicioFinanceiro && dataFimFinanceiro) {
      const inicio = new Date(dataInicioFinanceiro + 'T00:00:00')
      const fim = new Date(dataFimFinanceiro + 'T23:59:59')
      fim.setHours(23, 59, 59, 999) // Incluir o dia inteiro
      
      pagamentosFiltrados = pagamentos.filter(p => {
        const dataPag = new Date(p.dataPagamento || p.createdAt || new Date())
        return dataPag >= inicio && dataPag <= fim
      })
    }

    const linhasPagamentos = pagamentosFiltrados.map(pag => `
      <tr>
        <td style="padding: 6px 8px; border: 1px solid #d1d5db; font-family: monospace; font-size: 9px;">${pag.numeroComprovante || "-"}</td>
        <td style="padding: 6px 8px; border: 1px solid #d1d5db; font-size: 10px;">${pag.referenciaPagamento || "-"}</td>
        <td style="padding: 6px 8px; border: 1px solid #d1d5db; font-size: 10px;">${pag.nomePagador || "-"}</td>
        <td style="padding: 6px 8px; border: 1px solid #d1d5db; font-weight: 600; color: #16a34a; font-size: 10px;">R$ ${pag.valor ? parseFloat(pag.valor.toString().replace(",", ".")).toFixed(2).replace(".", ",") : "0,00"}</td>
        <td style="padding: 6px 8px; border: 1px solid #d1d5db; font-size: 10px;">${pag.dataPagamento ? new Date(pag.dataPagamento).toLocaleDateString("pt-BR") : pag.createdAt ? new Date(pag.createdAt).toLocaleDateString("pt-BR") : "-"}</td>
        <td style="padding: 6px 8px; border: 1px solid #d1d5db; text-align: center;">
          <span style="padding: 3px 8px; border-radius: 12px; font-size: 9px; font-weight: 600; ${
            pag.statusPagamento === "pago" ? "background: #dcfce7; color: #16a34a;" : 
            pag.statusPagamento === "pendente" ? "background: #fef3c7; color: #d97706;" : 
            "background: #f3f4f6; color: #6b7280;"
          }">
            ${pag.statusPagamento === "pago" ? "✓ Pago" : pag.statusPagamento === "pendente" ? "⏱ Pendente" : "✗ Sem Comprovante"}
          </span>
        </td>
      </tr>
    `).join("")

    return `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8">
          <title>Relatório Financeiro</title>
          <style>
            body { margin: 0; padding: 20px; font-family: Arial, sans-serif; }
            .header { text-align: center; margin-bottom: 15px; padding-bottom: 10px; border-bottom: 2px solid #004161; }
            .header h1 { color: #004161; margin: 0 0 5px 0; font-size: 20px; }
            .header p { color: #6b7280; margin: 2px 0; font-size: 11px; }
            .stats { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin: 15px 0; }
            .stat-card { background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%); padding: 12px; border-radius: 6px; text-align: center; border: 1px solid #fde68a; }
            .stat-number { font-size: 20px; font-weight: bold; color: #004161; margin-bottom: 4px; }
            .stat-label { font-size: 10px; color: #6b7280; }
            table { width: 100%; border-collapse: collapse; margin-top: 12px; font-size: 10px; }
            th { background: #004161; color: white; padding: 6px 8px; text-align: left; font-weight: 600; font-size: 10px; }
            td { padding: 6px 8px; border: 1px solid #d1d5db; font-size: 10px; }
            tr:nth-child(even) { background: #f9fafb; }
            tfoot { background: #f3f4f6; font-weight: bold; }
            .summary { background: #f9fafb; padding: 12px; border-radius: 6px; margin-top: 15px; border: 1px solid #e5e7eb; }
            .summary-item { display: flex; justify-content: space-between; padding: 6px 0; border-bottom: 1px solid #e5e7eb; font-size: 11px; }
            .summary-item:last-child { border-bottom: none; border-top: 2px solid #004161; padding-top: 8px; font-weight: bold; color: #16a34a; font-size: 13px; }
            .footer { margin-top: 20px; text-align: center; color: #9ca3af; font-size: 9px; border-top: 1px solid #e5e7eb; padding-top: 10px; }
            @media print { body { padding: 10px; } }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>💰 RELATÓRIO FINANCEIRO DE PAGAMENTOS</h1>
            <p>Sistema de Gerenciamento Municipal</p>
            <p>Gerado em ${new Date().toLocaleDateString("pt-BR")} às ${new Date().toLocaleTimeString("pt-BR")}</p>
            ${dataInicioFinanceiro && dataFimFinanceiro ? `<p style="color: #004161; font-weight: 600;">Período: ${new Date(dataInicioFinanceiro + 'T00:00:00').toLocaleDateString("pt-BR")} a ${new Date(dataFimFinanceiro + 'T23:59:59').toLocaleDateString("pt-BR")}</p>` : ""}
          </div>

          <div class="stats">
            <div class="stat-card">
              <div class="stat-number">${pagamentosFiltrados.filter(p => p.statusPagamento === "pago").length}</div>
              <div class="stat-label">Pagamentos Confirmados</div>
            </div>
            <div class="stat-card">
              <div class="stat-number">${pagamentosFiltrados.filter(p => p.statusPagamento === "pendente").length}</div>
              <div class="stat-label">Pagamentos Pendentes</div>
            </div>
            <div class="stat-card">
              <div class="stat-number">R$ ${pagamentosFiltrados.filter(p => p.statusPagamento === "pago").reduce((acc, p) => acc + parseFloat(p.valor?.toString().replace(",", ".") || "0"), 0).toFixed(2).replace(".", ",")}</div>
              <div class="stat-label">Valor Total Recebido</div>
            </div>
          </div>

          <h2 style="color: #004161; margin-top: 40px; margin-bottom: 20px; font-size: 20px;">Detalhamento de Pagamentos</h2>
          
          ${pagamentosFiltrados.length === 0 ? '<p style="text-align: center; color: #9ca3af; padding: 40px;">Nenhum pagamento registrado no período</p>' : `
            <table>
              <thead>
                <tr>
                  <th style="width: 12%;">Nº Pag.</th>
                  <th style="width: 25%;">Referente</th>
                  <th style="width: 25%;">Pagador</th>
                  <th style="width: 13%;">Valor</th>
                  <th style="width: 13%;">Data</th>
                  <th style="text-align: center; width: 12%;">Status</th>
                </tr>
              </thead>
              <tbody>
                ${linhasPagamentos}
              </tbody>
              <tfoot>
                <tr>
                  <td colspan="3" style="padding: 8px; border: 1px solid #d1d5db; text-align: right; font-size: 11px; font-weight: bold;">Total Recebido:</td>
                  <td style="padding: 8px; border: 1px solid #d1d5db; color: #16a34a; font-size: 12px; font-weight: bold;">R$ ${pagamentosFiltrados.filter(p => p.statusPagamento === "pago").reduce((acc, p) => acc + parseFloat(p.valor?.toString().replace(",", ".") || "0"), 0).toFixed(2).replace(".", ",")}</td>
                  <td colspan="2" style="padding: 8px; border: 1px solid #d1d5db;"></td>
                </tr>
              </tfoot>
            </table>
          `}

          <div class="summary">
            <h3 style="color: #004161; margin-top: 0; margin-bottom: 20px;">Resumo Financeiro</h3>
            <div class="summary-item">
              <span>Total de Pagamentos:</span>
              <span>${pagamentosFiltrados.length}</span>
            </div>
            <div class="summary-item">
              <span>Pagamentos Confirmados:</span>
              <span style="color: #16a34a;">${pagamentosFiltrados.filter(p => p.statusPagamento === "pago").length}</span>
            </div>
            <div class="summary-item">
              <span>Pagamentos Pendentes:</span>
              <span style="color: #d97706;">${pagamentosFiltrados.filter(p => p.statusPagamento === "pendente").length}</span>
            </div>
            <div class="summary-item">
              <span>Taxa de Conversão:</span>
              <span>${pagamentosFiltrados.length > 0 ? ((pagamentosFiltrados.filter(p => p.statusPagamento === "pago").length / pagamentosFiltrados.length) * 100).toFixed(1) : 0}%</span>
            </div>
            <div class="summary-item">
              <span>Valor Total Recebido:</span>
              <span>R$ ${pagamentosFiltrados.filter(p => p.statusPagamento === "pago").reduce((acc, p) => acc + parseFloat(p.valor?.toString().replace(",", ".") || "0"), 0).toFixed(2).replace(".", ",")}</span>
            </div>
          </div>

          <div class="footer">
            <p>Documento gerado automaticamente pelo Sistema EMIT</p>
            <p>Este documento possui validade institucional</p>
          </div>

          <script>
            window.onload = function() {
              setTimeout(function() { window.print(); }, 500);
            }
          </script>
        </body>
      </html>
    `
  }

  const fecharModal = () => {
    setModalAberto(null)
  }

  // Calcular estatísticas
  const totalGuias = guias.length
  const totalPagamentos = pagamentos.length
  const totalFeirantes = feirantes.length
  const totalAforamentos = aforamentos.length
  const pagamentosPagos = pagamentos.filter(p => p.statusPagamento === "pago").length
  const pagamentosPendentes = pagamentos.filter(p => p.statusPagamento === "pendente").length
  
  const valorTotalPagamentos = pagamentos
    .filter(p => p.statusPagamento === "pago")
    .reduce((acc, p) => {
      const valor = p.valor?.toString().replace(",", ".") || "0"
      return acc + parseFloat(valor)
    }, 0)

  // Estatísticas mensais (mês atual)
  const mesAtual = new Date().getMonth()
  const anoAtual = new Date().getFullYear()
  
  const guiasMesAtual = guias.filter(g => {
    const data = new Date(g.createdAt || new Date())
    return data.getMonth() === mesAtual && data.getFullYear() === anoAtual
  }).length

  const pagamentosMesAtual = pagamentos.filter(p => {
    const data = new Date(p.createdAt || new Date())
    return data.getMonth() === mesAtual && data.getFullYear() === anoAtual && p.statusPagamento === "pago"
  })

  const valorMesAtual = pagamentosMesAtual.reduce((acc, p) => {
    const valor = p.valor?.toString().replace(",", ".") || "0"
    return acc + parseFloat(valor)
  }, 0)

  // Estatísticas anuais
  const guiasAnoAtual = guias.filter(g => {
    const data = new Date(g.createdAt || new Date())
    return data.getFullYear() === anoAtual
  }).length

  const pagamentosAnoAtual = pagamentos.filter(p => {
    const data = new Date(p.createdAt || new Date())
    return data.getFullYear() === anoAtual && p.statusPagamento === "pago"
  })

  const valorAnoAtual = pagamentosAnoAtual.reduce((acc, p) => {
    const valor = p.valor?.toString().replace(",", ".") || "0"
    return acc + parseFloat(valor)
  }, 0)

  // Dados para o gráfico (últimos 12 meses)
  const mesesNomes = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"]
  const guiasPorMes = Array(12).fill(0)
  
  guias.forEach(g => {
    const data = new Date(g.createdAt || new Date())
    if (data.getFullYear() === anoAtual) {
      guiasPorMes[data.getMonth()]++
    }
  })

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-lg p-6 border border-gray-200"
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="p-3 bg-[#004161] rounded-lg">
            <BarChart3 className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-800">Relatórios</h3>
            <p className="text-sm text-gray-600">Gere relatórios e estatísticas do sistema</p>
          </div>
        </div>

        {/* Cards de Estatísticas Gerais */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border border-blue-200">
            <div className="flex items-center justify-between mb-2">
              <FileText className="w-8 h-8 text-blue-600" />
              <span className="text-2xl font-bold text-blue-700">{totalGuias}</span>
            </div>
            <p className="text-sm text-gray-700 font-medium">Total de Guias</p>
          </div>

          <div className="p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-lg border border-green-200">
            <div className="flex items-center justify-between mb-2">
              <DollarSign className="w-8 h-8 text-green-600" />
              <span className="text-2xl font-bold text-green-700">{pagamentosPagos}</span>
            </div>
            <p className="text-sm text-gray-700 font-medium">Pagamentos Confirmados</p>
          </div>

          <div className="p-4 bg-gradient-to-br from-teal-50 to-teal-100 rounded-lg border border-teal-200">
            <div className="flex items-center justify-between mb-2">
              <Users className="w-8 h-8 text-teal-600" />
              <span className="text-2xl font-bold text-teal-700">{totalFeirantes}</span>
            </div>
            <p className="text-sm text-gray-700 font-medium">Feirantes Cadastrados</p>
          </div>

          <div className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg border border-purple-200">
            <div className="flex items-center justify-between mb-2">
              <FileCheck className="w-8 h-8 text-purple-600" />
              <span className="text-2xl font-bold text-purple-700">{totalAforamentos}</span>
            </div>
            <p className="text-sm text-gray-700 font-medium">Aforamentos Registrados</p>
          </div>
        </div>

        {/* Botões de Relatórios */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <motion.div
            whileHover={{ scale: 1.02 }}
            className="p-6 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl border border-blue-200 cursor-pointer"
            onClick={gerarRelatorioGuias}
          >
            <FileText className="w-10 h-10 text-blue-600 mb-4" />
            <h4 className="text-lg font-semibold text-gray-800 mb-2">Relatório de Guias</h4>
            <p className="text-sm text-gray-600 mb-4">
              Visualize todas as guias emitidas no sistema
            </p>
            <button className="flex items-center gap-2 text-blue-600 text-sm font-medium hover:text-blue-700">
              <Download className="w-4 h-4" />
              Gerar Relatório
            </button>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.02 }}
            className="p-6 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border border-green-200 cursor-pointer"
            onClick={gerarRelatorioMensal}
          >
            <Calendar className="w-10 h-10 text-green-600 mb-4" />
            <h4 className="text-lg font-semibold text-gray-800 mb-2">Relatório Mensal</h4>
            <p className="text-sm text-gray-600 mb-4">
              Estatísticas do mês atual ({mesesNomes[mesAtual]} {anoAtual})
            </p>
            <button className="flex items-center gap-2 text-green-600 text-sm font-medium hover:text-green-700">
              <Download className="w-4 h-4" />
              Gerar Relatório
            </button>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.02 }}
            className="p-6 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl border border-purple-200 cursor-pointer"
            onClick={gerarRelatorioAnual}
          >
            <BarChart3 className="w-10 h-10 text-purple-600 mb-4" />
            <h4 className="text-lg font-semibold text-gray-800 mb-2">Relatório Anual</h4>
            <p className="text-sm text-gray-600 mb-4">
              Estatísticas completas de {anoAtual}
            </p>
            <button className="flex items-center gap-2 text-purple-600 text-sm font-medium hover:text-purple-700">
              <Download className="w-4 h-4" />
              Gerar Relatório
            </button>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.02 }}
            className="p-6 bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl border border-amber-200 cursor-pointer"
            onClick={gerarRelatorioFinanceiro}
          >
            <DollarSign className="w-10 h-10 text-amber-600 mb-4" />
            <h4 className="text-lg font-semibold text-gray-800 mb-2">Relatório Financeiro</h4>
            <p className="text-sm text-gray-600 mb-4">
              Detalhamento de todos os pagamentos
            </p>
            <button className="flex items-center gap-2 text-amber-600 text-sm font-medium hover:text-amber-700">
              <Download className="w-4 h-4" />
              Gerar Relatório
            </button>
          </motion.div>
        </div>

        {/* Gráfico de Exemplo */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-8 p-6 bg-gray-50 rounded-xl border border-gray-200"
        >
          <h4 className="text-lg font-semibold text-gray-800 mb-4">Guias por Mês ({anoAtual})</h4>
          <div className="h-64 flex items-end justify-around gap-4">
            {guiasPorMes.map((value, index) => {
              const maxValue = Math.max(...guiasPorMes, 1)
              const height = (value / maxValue) * 100
              
              return (
                <div key={index} className="flex-1 flex flex-col items-center">
                  <motion.div
                    initial={{ height: 0 }}
                    animate={{ height: `${height}%` }}
                    transition={{ delay: index * 0.1, duration: 0.5 }}
                    className="w-full bg-gradient-to-t from-[#004161] to-[#0066a1] rounded-t-lg min-h-[20px] relative group"
                  >
                    <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity bg-gray-800 text-white text-xs px-2 py-1 rounded">
                      {value}
                    </div>
                  </motion.div>
                  <span className="text-xs text-gray-600 mt-2">
                    {mesesNomes[index]}
                  </span>
                </div>
              )
            })}
          </div>
        </motion.div>
      </motion.div>

      {/* Modais de Relatórios */}
      <AnimatePresence>
        {modalAberto && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={fecharModal}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header do Modal */}
              <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between no-print">
                <h3 className="text-2xl font-bold text-gray-800">
                  {modalAberto === "guias" && "Relatório de Guias"}
                  {modalAberto === "mensal" && `Relatório Mensal - ${mesesNomes[mesAtual]} ${anoAtual}`}
                  {modalAberto === "anual" && `Relatório Anual - ${anoAtual}`}
                  {modalAberto === "financeiro" && "Relatório Financeiro de Pagamentos"}
                </h3>
                <div className="flex items-center gap-2">
                  <button
                    onClick={imprimirRelatorio}
                    className="px-4 py-2 bg-[#004161] text-white rounded-lg hover:bg-[#003050] transition-colors flex items-center gap-2"
                  >
                    <Download className="w-4 h-4" />
                    Imprimir
                  </button>
                  <button
                    onClick={fecharModal}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <X className="w-6 h-6 text-gray-600" />
                  </button>
                </div>
              </div>

              {/* Conteúdo do Modal */}
              <div className="p-8">
                {/* Relatório de Guias */}
                {modalAberto === "guias" && (
                  <div className="space-y-6">
                    <div className="text-center mb-6">
                      <h2 className="text-3xl font-bold text-gray-800 mb-2">Relatório Completo de Guias</h2>
                      <p className="text-gray-600">Gerado em {new Date().toLocaleDateString("pt-BR")}</p>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                      <div className="text-center p-4 bg-blue-50 rounded-lg">
                        <p className="text-3xl font-bold text-blue-700">{totalGuias}</p>
                        <p className="text-sm text-gray-600">Total de Guias</p>
                      </div>
                      <div className="text-center p-4 bg-green-50 rounded-lg">
                        <p className="text-3xl font-bold text-green-700">{pagamentosPagos}</p>
                        <p className="text-sm text-gray-600">Pagamentos Confirmados</p>
                      </div>
                      <div className="text-center p-4 bg-amber-50 rounded-lg">
                        <p className="text-3xl font-bold text-amber-700">{pagamentosPendentes}</p>
                        <p className="text-sm text-gray-600">Pagamentos Pendentes</p>
                      </div>
                      <div className="text-center p-4 bg-purple-50 rounded-lg">
                        <p className="text-3xl font-bold text-purple-700">
                          R$ {valorTotalPagamentos.toFixed(2).replace(".", ",")}
                        </p>
                        <p className="text-sm text-gray-600">Valor Total Recebido</p>
                      </div>
                    </div>

                    <div className="border-t pt-6">
                      <h4 className="font-semibold text-lg mb-4">Lista de Guias Emitidas</h4>
                      {guias.length === 0 ? (
                        <p className="text-gray-500 text-center py-8">Nenhuma guia cadastrada</p>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm border-collapse">
                            <thead className="bg-gray-100">
                              <tr>
                                <th className="px-4 py-3 text-left border">Nº Óbito</th>
                                <th className="px-4 py-3 text-left border">Nome do Falecido</th>
                                <th className="px-4 py-3 text-left border">Data Sepultamento</th>
                                <th className="px-4 py-3 text-left border">Cemitério</th>
                              </tr>
                            </thead>
                            <tbody>
                              {guias.map((guia, index) => (
                                <tr key={guia._id || index} className="hover:bg-gray-50">
                                  <td className="px-4 py-3 border">{guia.numeroObito || "-"}</td>
                                  <td className="px-4 py-3 border">{guia.nomeFalecido || "-"}</td>
                                  <td className="px-4 py-3 border">{guia.dataSepultamento || "-"}</td>
                                  <td className="px-4 py-3 border">{guia.cemiterio || "-"}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Relatório Mensal */}
                {modalAberto === "mensal" && (
                  <div className="space-y-6">
                    <div className="text-center mb-6">
                      <h2 className="text-3xl font-bold text-gray-800 mb-2">
                        Relatório Mensal - {mesesNomes[mesAtual]} {anoAtual}
                      </h2>
                      <p className="text-gray-600">Gerado em {new Date().toLocaleDateString("pt-BR")}</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                      <div className="text-center p-6 bg-blue-50 rounded-lg">
                        <TrendingUp className="w-10 h-10 text-blue-600 mx-auto mb-2" />
                        <p className="text-3xl font-bold text-blue-700">{guiasMesAtual}</p>
                        <p className="text-sm text-gray-600">Guias no Mês</p>
                      </div>
                      <div className="text-center p-6 bg-green-50 rounded-lg">
                        <DollarSign className="w-10 h-10 text-green-600 mx-auto mb-2" />
                        <p className="text-3xl font-bold text-green-700">{pagamentosMesAtual.length}</p>
                        <p className="text-sm text-gray-600">Pagamentos Confirmados</p>
                      </div>
                      <div className="text-center p-6 bg-purple-50 rounded-lg">
                        <FileCheck className="w-10 h-10 text-purple-600 mx-auto mb-2" />
                        <p className="text-3xl font-bold text-purple-700">
                          R$ {valorMesAtual.toFixed(2).replace(".", ",")}
                        </p>
                        <p className="text-sm text-gray-600">Valor Recebido</p>
                      </div>
                    </div>

                    <div className="border-t pt-6">
                      <h4 className="font-semibold text-lg mb-4">Guias do Mês</h4>
                      {guiasMesAtual === 0 ? (
                        <p className="text-gray-500 text-center py-8">Nenhuma guia emitida neste mês</p>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm border-collapse">
                            <thead className="bg-gray-100">
                              <tr>
                                <th className="px-4 py-3 text-left border">Nº Óbito</th>
                                <th className="px-4 py-3 text-left border">Nome do Falecido</th>
                                <th className="px-4 py-3 text-left border">Data Sepultamento</th>
                                <th className="px-4 py-3 text-left border">Cemitério</th>
                              </tr>
                            </thead>
                            <tbody>
                              {guias.filter(g => {
                                const data = new Date(g.createdAt || new Date())
                                return data.getMonth() === mesAtual && data.getFullYear() === anoAtual
                              }).map((guia, index) => (
                                <tr key={guia._id || index} className="hover:bg-gray-50">
                                  <td className="px-4 py-3 border">{guia.numeroObito || "-"}</td>
                                  <td className="px-4 py-3 border">{guia.nomeFalecido || "-"}</td>
                                  <td className="px-4 py-3 border">{guia.dataSepultamento || "-"}</td>
                                  <td className="px-4 py-3 border">{guia.cemiterio || "-"}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>

                    <div className="border-t pt-6 mt-6">
                      <h4 className="font-semibold text-lg mb-4">Resumo do Período</h4>
                      <div className="bg-gray-50 p-6 rounded-lg space-y-3">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Total de Guias Emitidas:</span>
                          <span className="font-semibold">{guiasMesAtual}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Pagamentos Confirmados:</span>
                          <span className="font-semibold">{pagamentosMesAtual.length}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Valor Total Recebido:</span>
                          <span className="font-semibold">R$ {valorMesAtual.toFixed(2).replace(".", ",")}</span>
                        </div>
                        <div className="flex justify-between border-t pt-3">
                          <span className="text-gray-600">Taxa de Conversão:</span>
                          <span className="font-semibold">
                            {guiasMesAtual > 0 ? ((pagamentosMesAtual.length / guiasMesAtual) * 100).toFixed(1) : 0}%
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Relatório Anual */}
                {modalAberto === "anual" && (
                  <div className="space-y-6">
                    <div className="text-center mb-6">
                      <h2 className="text-3xl font-bold text-gray-800 mb-2">Relatório Anual - {anoAtual}</h2>
                      <p className="text-gray-600">Gerado em {new Date().toLocaleDateString("pt-BR")}</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
                      <div className="text-center p-6 bg-blue-50 rounded-lg">
                        <FileText className="w-10 h-10 text-blue-600 mx-auto mb-2" />
                        <p className="text-3xl font-bold text-blue-700">{guiasAnoAtual}</p>
                        <p className="text-sm text-gray-600">Guias no Ano</p>
                      </div>
                      <div className="text-center p-6 bg-green-50 rounded-lg">
                        <DollarSign className="w-10 h-10 text-green-600 mx-auto mb-2" />
                        <p className="text-3xl font-bold text-green-700">{pagamentosAnoAtual.length}</p>
                        <p className="text-sm text-gray-600">Pagamentos Confirmados</p>
                      </div>
                      <div className="text-center p-6 bg-purple-50 rounded-lg">
                        <FileCheck className="w-10 h-10 text-purple-600 mx-auto mb-2" />
                        <p className="text-3xl font-bold text-purple-700">
                          R$ {valorAnoAtual.toFixed(2).replace(".", ",")}
                        </p>
                        <p className="text-sm text-gray-600">Valor Total Recebido</p>
                      </div>
                      <div className="text-center p-6 bg-teal-50 rounded-lg">
                        <Users className="w-10 h-10 text-teal-600 mx-auto mb-2" />
                        <p className="text-3xl font-bold text-teal-700">{totalFeirantes}</p>
                        <p className="text-sm text-gray-600">Feirantes Ativos</p>
                      </div>
                    </div>

                    <div className="border-t pt-6">
                      <h4 className="font-semibold text-lg mb-4">Distribuição Mensal de Guias</h4>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm border-collapse">
                          <thead className="bg-gray-100">
                            <tr>
                              <th className="px-4 py-3 text-left border">Mês</th>
                              <th className="px-4 py-3 text-center border">Quantidade</th>
                              <th className="px-4 py-3 text-center border">Porcentagem</th>
                            </tr>
                          </thead>
                          <tbody>
                            {guiasPorMes.map((qtd, index) => (
                              <tr key={index} className="hover:bg-gray-50">
                                <td className="px-4 py-3 border">{mesesNomes[index]}</td>
                                <td className="px-4 py-3 text-center border">{qtd}</td>
                                <td className="px-4 py-3 text-center border">
                                  {guiasAnoAtual > 0 ? ((qtd / guiasAnoAtual) * 100).toFixed(1) : 0}%
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot className="bg-gray-100 font-semibold">
                            <tr>
                              <td className="px-4 py-3 border">Total</td>
                              <td className="px-4 py-3 text-center border">{guiasAnoAtual}</td>
                              <td className="px-4 py-3 text-center border">100%</td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </div>

                    <div className="border-t pt-6 mt-6">
                      <h4 className="font-semibold text-lg mb-4">Guias do Ano</h4>
                      {guiasAnoAtual === 0 ? (
                        <p className="text-gray-500 text-center py-8">Nenhuma guia emitida neste ano</p>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm border-collapse">
                            <thead className="bg-gray-100">
                              <tr>
                                <th className="px-4 py-3 text-left border">Nº Óbito</th>
                                <th className="px-4 py-3 text-left border">Nome do Falecido</th>
                                <th className="px-4 py-3 text-left border">Data Sepultamento</th>
                                <th className="px-4 py-3 text-left border">Cemitério</th>
                              </tr>
                            </thead>
                            <tbody>
                              {guias.filter(g => {
                                const data = new Date(g.createdAt || new Date())
                                return data.getFullYear() === anoAtual
                              }).map((guia, index) => (
                                <tr key={guia._id || index} className="hover:bg-gray-50">
                                  <td className="px-4 py-3 border">{guia.numeroObito || "-"}</td>
                                  <td className="px-4 py-3 border">{guia.nomeFalecido || "-"}</td>
                                  <td className="px-4 py-3 border">{guia.dataSepultamento || "-"}</td>
                                  <td className="px-4 py-3 border">{guia.cemiterio || "-"}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Relatório Financeiro */}
                {modalAberto === "financeiro" && (
                  <div className="space-y-6">
                    <div className="text-center mb-6">
                      <h2 className="text-3xl font-bold text-gray-800 mb-2">Relatório Financeiro de Pagamentos</h2>
                      <p className="text-gray-600">Gerado em {new Date().toLocaleDateString("pt-BR")}</p>
                    </div>

                    {/* Filtros de Período */}
                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-6">
                      <h4 className="font-semibold text-gray-800 mb-3">Filtrar por Período</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Data Início</label>
                          <input
                            type="date"
                            value={dataInicioFinanceiro}
                            onChange={(e) => setDataInicioFinanceiro(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#004161] focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Data Fim</label>
                          <input
                            type="date"
                            value={dataFimFinanceiro}
                            onChange={(e) => setDataFimFinanceiro(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#004161] focus:border-transparent"
                          />
                        </div>
                      </div>
                      {dataInicioFinanceiro && dataFimFinanceiro && (
                        <p className="text-sm text-blue-700 font-medium mt-3">
                          📅 Exibindo período de {new Date(dataInicioFinanceiro + 'T00:00:00').toLocaleDateString("pt-BR")} a {new Date(dataFimFinanceiro + 'T23:59:59').toLocaleDateString("pt-BR")}
                        </p>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                      <div className="text-center p-6 bg-green-50 rounded-lg border border-green-200">
                        <DollarSign className="w-10 h-10 text-green-600 mx-auto mb-2" />
                        <p className="text-3xl font-bold text-green-700">
                          {dataInicioFinanceiro && dataFimFinanceiro
                            ? pagamentos.filter(p => {
                                const inicio = new Date(dataInicioFinanceiro + 'T00:00:00')
                                const fim = new Date(dataFimFinanceiro + 'T23:59:59')
                                fim.setHours(23, 59, 59, 999)
                                const dataPag = new Date(p.dataPagamento || p.createdAt || new Date())
                                return dataPag >= inicio && dataPag <= fim && p.statusPagamento === "pago"
                              }).length
                            : pagamentosPagos
                          }
                        </p>
                        <p className="text-sm text-gray-600">Pagamentos Confirmados</p>
                      </div>
                      <div className="text-center p-6 bg-amber-50 rounded-lg border border-amber-200">
                        <Calendar className="w-10 h-10 text-amber-600 mx-auto mb-2" />
                        <p className="text-3xl font-bold text-amber-700">
                          {dataInicioFinanceiro && dataFimFinanceiro
                            ? pagamentos.filter(p => {
                                const inicio = new Date(dataInicioFinanceiro + 'T00:00:00')
                                const fim = new Date(dataFimFinanceiro + 'T23:59:59')
                                fim.setHours(23, 59, 59, 999)
                                const dataPag = new Date(p.dataPagamento || p.createdAt || new Date())
                                return dataPag >= inicio && dataPag <= fim && p.statusPagamento === "pendente"
                              }).length
                            : pagamentosPendentes
                          }
                        </p>
                        <p className="text-sm text-gray-600">Pagamentos Pendentes</p>
                      </div>
                      <div className="text-center p-6 bg-purple-50 rounded-lg border border-purple-200">
                        <TrendingUp className="w-10 h-10 text-purple-600 mx-auto mb-2" />
                        <p className="text-3xl font-bold text-purple-700">
                          R$ {(dataInicioFinanceiro && dataFimFinanceiro
                            ? pagamentos.filter(p => {
                                const inicio = new Date(dataInicioFinanceiro + 'T00:00:00')
                                const fim = new Date(dataFimFinanceiro + 'T23:59:59')
                                fim.setHours(23, 59, 59, 999)
                                const dataPag = new Date(p.dataPagamento || p.createdAt || new Date())
                                return dataPag >= inicio && dataPag <= fim && p.statusPagamento === "pago"
                              }).reduce((acc, p) => acc + parseFloat(p.valor?.toString().replace(",", ".") || "0"), 0)
                            : valorTotalPagamentos
                          ).toFixed(2).replace(".", ",")}
                        </p>
                        <p className="text-sm text-gray-600">Valor Total Recebido</p>
                      </div>
                    </div>

                    <div className="border-t pt-6">
                      <h4 className="font-semibold text-lg mb-4">Detalhamento de Pagamentos</h4>
                      {(() => {
                        const pagamentosFiltrados = dataInicioFinanceiro && dataFimFinanceiro
                          ? pagamentos.filter(p => {
                              const inicio = new Date(dataInicioFinanceiro + 'T00:00:00')
                              const fim = new Date(dataFimFinanceiro + 'T23:59:59')
                              fim.setHours(23, 59, 59, 999)
                              const dataPag = new Date(p.dataPagamento || p.createdAt || new Date())
                              return dataPag >= inicio && dataPag <= fim
                            })
                          : pagamentos
                        
                        return pagamentosFiltrados.length === 0 ? (
                          <p className="text-gray-500 text-center py-8">Nenhum pagamento registrado{dataInicioFinanceiro && dataFimFinanceiro ? " no período" : ""}</p>
                        ) : (
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm border-collapse">
                              <thead className="bg-gray-100">
                                <tr>
                                  <th className="px-4 py-3 text-left border">Nº Pagamento</th>
                                  <th className="px-4 py-3 text-left border">Referente a</th>
                                  <th className="px-4 py-3 text-left border">Pagador</th>
                                  <th className="px-4 py-3 text-left border">Valor</th>
                                  <th className="px-4 py-3 text-left border">Data</th>
                                  <th className="px-4 py-3 text-center border">Status</th>
                                </tr>
                              </thead>
                              <tbody>
                                {pagamentosFiltrados.map((pag, index) => (
                                  <tr key={pag._id || index} className="hover:bg-gray-50">
                                    <td className="px-4 py-3 border font-mono text-xs">{pag.numeroComprovante || "-"}</td>
                                    <td className="px-4 py-3 border">{pag.referenciaPagamento || "-"}</td>
                                    <td className="px-4 py-3 border">{pag.nomePagador || "-"}</td>
                                    <td className="px-4 py-3 border font-semibold text-green-700">
                                      R$ {pag.valor ? parseFloat(pag.valor.toString().replace(",", ".")).toFixed(2).replace(".", ",") : "0,00"}
                                    </td>
                                    <td className="px-4 py-3 border">
                                      {pag.dataPagamento 
                                        ? new Date(pag.dataPagamento).toLocaleDateString("pt-BR")
                                        : pag.createdAt 
                                          ? new Date(pag.createdAt).toLocaleDateString("pt-BR")
                                          : "-"
                                      }
                                    </td>
                                    <td className="px-4 py-3 border text-center">
                                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                        pag.statusPagamento === "pago" 
                                          ? "bg-green-100 text-green-700" 
                                          : pag.statusPagamento === "pendente"
                                          ? "bg-amber-100 text-amber-700"
                                          : "bg-gray-100 text-gray-700"
                                      }`}>
                                        {pag.statusPagamento === "pago" ? "✓ Pago" : 
                                         pag.statusPagamento === "pendente" ? "⏱ Pendente" : 
                                         "✗ Sem Comprovante"}
                                      </span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                              <tfoot className="bg-gray-100 font-semibold">
                                <tr>
                                  <td colSpan={3} className="px-4 py-3 border text-right">Total Recebido:</td>
                                  <td className="px-4 py-3 border text-green-700 font-bold">
                                    R$ {pagamentosFiltrados
                                      .filter(p => p.statusPagamento === "pago")
                                      .reduce((acc, p) => acc + parseFloat(p.valor?.toString().replace(",", ".") || "0"), 0)
                                      .toFixed(2).replace(".", ",")}
                                  </td>
                                  <td colSpan={2} className="px-4 py-3 border"></td>
                                </tr>
                              </tfoot>
                            </table>
                          </div>
                        )
                      })()}
                    </div>

                    <div className="border-t pt-6 mt-6">
                      <h4 className="font-semibold text-lg mb-4">Resumo Financeiro</h4>
                      <div className="bg-gray-50 p-6 rounded-lg space-y-3">
                        {(() => {
                          const pagamentosFiltrados = dataInicioFinanceiro && dataFimFinanceiro
                            ? pagamentos.filter(p => {
                                const inicio = new Date(dataInicioFinanceiro + 'T00:00:00')
                                const fim = new Date(dataFimFinanceiro + 'T23:59:59')
                                fim.setHours(23, 59, 59, 999)
                                const dataPag = new Date(p.dataPagamento || p.createdAt || new Date())
                                return dataPag >= inicio && dataPag <= fim
                              })
                            : pagamentos
                          
                          const totalFiltrado = pagamentosFiltrados.length
                          const pagosFiltrado = pagamentosFiltrados.filter(p => p.statusPagamento === "pago").length
                          const pendentesFiltrado = pagamentosFiltrados.filter(p => p.statusPagamento === "pendente").length
                          const valorFiltrado = pagamentosFiltrados
                            .filter(p => p.statusPagamento === "pago")
                            .reduce((acc, p) => acc + parseFloat(p.valor?.toString().replace(",", ".") || "0"), 0)
                          
                          return (
                            <>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Total de Pagamentos:</span>
                                <span className="font-semibold">{totalFiltrado}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Pagamentos Confirmados:</span>
                                <span className="font-semibold text-green-700">{pagosFiltrado}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Pagamentos Pendentes:</span>
                                <span className="font-semibold text-amber-700">{pendentesFiltrado}</span>
                              </div>
                              <div className="flex justify-between border-t pt-3">
                                <span className="text-gray-600">Valor Total Recebido:</span>
                                <span className="font-semibold text-green-700 text-lg">
                                  R$ {valorFiltrado.toFixed(2).replace(".", ",")}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Taxa de Conversão:</span>
                                <span className="font-semibold">
                                  {totalFiltrado > 0 ? ((pagosFiltrado / totalFiltrado) * 100).toFixed(1) : 0}%
                                </span>
                              </div>
                            </>
                          )
                        })()}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
